<template>
  <section class="w-100 position-relative">

    <!-- Grid de miniaturas de videos -->
    <div class="grid-container mb-2">
      <div
        class="grid-item"
        v-for="(video, index) in visible_videos"
        :key="index"
      >
        <img
          :src="video.thumbnail_url"
          class="img-cover"
          @error="onImgError($event)"
          @click="openVideo(video.video_url)"
        />
        <div class="container-icon-heart" v-if="video.likes || video.views">
          <p v-if="video.views" class="d-flex align-items-center">
            <feather-icon icon="EyeIcon" class="mr-05"></feather-icon>
            <span>{{ getFormat(video.views) }}</span>
          </p>
          <p v-if="video.likes" class="d-flex align-items-center">
            <feather-icon icon="HeartIcon" class="mr-05"></feather-icon>
            <span>{{ getFormat(video.likes) }}</span>
          </p>
        </div>
        <div class="badge-content-type" v-if="video.creator_content_type === 'SHORTS'">
          <span>Shorts</span>
        </div>
      </div>
    </div>

    <!-- Métricas avanzadas (private metrics disponibles) -->
    <template v-if="private_metrics">
      <!-- Tabs -->
      <div class="tab-switcher mb-2">
        <button
          v-for="tab in available_tabs"
          :key="tab.key"
          class="tab-btn"
          :class="{ active: active_tab === tab.key }"
          @click="active_tab = tab.key"
        >
          <img v-if="tab.key === 'SHORTS'" :src="TAB_SVG_ICONS['SHORTS']" width="14" height="14" class="mr-05" />
          <img v-else-if="tab.key === 'VOD'" :src="TAB_SVG_ICONS['VOD']" width="14" height="14" class="mr-05" />
          <feather-icon v-else :icon="TAB_ICONS[tab.key]" size="14" class="mr-05"></feather-icon>
          {{ tab.label }}
        </button>
      </div>

      <!-- Totales del período -->
      <p class="section-label mb-05">Totales del período</p>
      <div class="grid-analytics mb-2">
        <div v-for="(item, index) in active_totals" :key="index" class="box-style-analytics">
          <div class="d-flex align-items-center gap-1 mb-05">
            <span class="avenir-medium label-metric">{{ item.label }}</span>
            <span v-if="item.tooltip" :id="`yt-totals-info-${index}`">
              <feather-icon icon="InfoIcon" size="13" class="text-muted cursor-pointer" />
              <b-popover :target="`yt-totals-info-${index}`" triggers="hover" placement="top">
                <span v-html="item.tooltip"></span>
              </b-popover>
            </span>
          </div>
          <span class="title-result">{{ item.value }}</span>
        </div>
      </div>

      <!-- Promedios por video -->
      <p class="section-label mb-05">Promedios por video</p>
      <div class="grid-analytics mb-2">
        <div v-for="(item, index) in active_averages" :key="index" class="box-style-analytics">
          <div class="d-flex align-items-center gap-1 mb-05">
            <span class="avenir-medium label-metric">{{ item.label }}</span>
            <span v-if="item.tooltip" :id="`yt-avgs-info-${index}`">
              <feather-icon icon="InfoIcon" size="13" class="text-muted cursor-pointer" />
              <b-popover :target="`yt-avgs-info-${index}`" triggers="hover" placement="top">
                <span v-html="item.tooltip"></span>
              </b-popover>
            </span>
          </div>
          <span class="title-result">{{ item.value }}</span>
        </div>
      </div>

      <!-- Demographics -->
      <div v-if="active_demographics" class="box-style-analytics mb-2">
        <!-- Género + Edad side by side -->
        <div v-if="active_demographics.has_gender || active_demographics.has_age" class="demo-row mb-2">
          <!-- Pie género -->
          <div v-if="active_demographics.has_gender" class="gender-section">
            <p class="avenir-medium label-metric mb-1">Género</p>
            <svg viewBox="0 0 120 120" class="pie-svg" aria-hidden="true">
              <path :d="active_demographics.male_path" fill="#7367f0" />
              <path :d="active_demographics.female_path" fill="#ea5455" />
            </svg>
            <div class="gender-legend">
              <div class="legend-item"><span class="dot dot--blue"></span> Hombres <b>{{ active_demographics.male_pct }}%</b></div>
              <div class="legend-item"><span class="dot dot--red"></span> Mujeres <b>{{ active_demographics.female_pct }}%</b></div>
            </div>
          </div>

          <!-- Line chart edades -->
          <div v-if="active_demographics.has_age" class="age-section">
            <p class="avenir-medium label-metric mb-1">Edad</p>
            <svg :viewBox="`0 0 ${ageChartW} ${ageChartH}`" class="age-svg" aria-hidden="true">
              <!-- grid lines -->
              <line v-for="g in ageGridLines" :key="g" :x1="ageMarginL" :y1="g" :x2="ageChartW - ageMarginR" :y2="g" stroke="#E2E8F0" stroke-width="1" />
              <!-- smooth line -->
              <path :d="active_demographics.age_path" fill="none" stroke="#7367f0" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
              <!-- dots -->
              <circle v-for="pt in active_demographics.age_points" :key="pt.label" :cx="pt.x" :cy="pt.y" r="3" fill="#7367f0" />
              <!-- x labels -->
              <text v-for="pt in active_demographics.age_points" :key="`l-${pt.label}`" :x="pt.x" :y="ageChartH - 2" text-anchor="middle" class="age-label-svg">{{ pt.label }}</text>
              <!-- y labels -->
              <text v-for="g in ageYLabels" :key="`y-${g.val}`" :x="ageMarginL - 3" :y="g.y + 4" text-anchor="end" class="age-label-svg">{{ g.val }}%</text>
            </svg>
          </div>
        </div>

        <!-- Países -->
        <div v-if="active_demographics.countries && active_demographics.countries.length">
          <p class="avenir-medium label-metric mb-05">Países principales</p>
          <div v-for="c in active_demographics.countries.slice(0, 3)" :key="c.region_code" class="d-flex justify-content-between f-12 mb-05">
            <span>{{ c.region_code }}</span>
            <span class="text-muted">{{ (c.percentage * 100).toFixed(1) }}%</span>
          </div>
        </div>
      </div>
    </template>

    <!-- Métricas básicas del canal (sin private metrics) -->
    <div v-else class="grid-analytics mb-2">
      <div v-for="(item, index) in channel_stats" :key="index" class="box-style-analytics">
        <div class="d-flex align-items-center gap-1 mb-05">
          <span class="avenir-medium label-metric">{{ item.label }}</span>
          <span v-if="item.tooltip" :id="`yt-stat-info-${index}`">
            <feather-icon icon="InfoIcon" size="13" class="text-muted cursor-pointer" />
            <b-popover :target="`yt-stat-info-${index}`" triggers="hover" placement="top">
              <span v-html="item.tooltip"></span>
            </b-popover>
          </span>
        </div>
        <span class="title-result">{{ item.value }}</span>
      </div>
    </div>

    <!-- Info del canal -->
    <div class="box-style-analytics mb-2">
      <div class="d-flex justify-content-between align-items-center mb-1 flex-wrap">
        <p class="f-12 m-0">
          <span class="text-uppercase text-blue text-underline cursor-pointer" @click="openChannel">@{{ channel.handle || channel.youtube_id }}</span>
          · {{ getFormat(channel.subscriber_count) }} <span class="text-uppercase">{{ $t('campaigns.followers') }}</span>
        </p>
        <b-button variant="outline-secondary-custom-analysis" @click="openChannel" class="outline-secondary-custom-analysis">
          <feather-icon icon="ExternalLinkIcon"></feather-icon>
        </b-button>
      </div>

      <h4>{{ channel.title }}</h4>
      <p class="avenir-medium" v-if="channel.description">{{ channel.description }}</p>

      <div v-if="topic_labels.length" class="mt-1">
        <p class="section-label mb-1">Categorías de contenido</p>
        <div class="category-grid">
          <div v-for="cat in topic_labels" :key="cat.label" class="category-item">
            <feather-icon :icon="cat.icon" size="20" class="category-icon" />
            <span class="category-name">{{ cat.label }}</span>
          </div>
        </div>
      </div>

      <b-button
        v-if="channel.has_private_metrics && !private_metrics"
        class="blue-button w-100 mt-2"
        variant="blue-button"
        @click="$emit('get_profile_url')"
      >
        {{ $t('search_preview.seeFullReport') }}
      </b-button>
    </div>

  </section>
</template>

<script>
import { getFormat } from '@/libs/utils/formats';
import { BButton, BPopover } from 'bootstrap-vue';

const TAB_LABELS = { OVERALL: 'General', VOD: 'Videos', SHORTS: 'Shorts' };
const TAB_ICONS = { OVERALL: 'BarChart2Icon', VOD: 'PlayCircleIcon', SHORTS: 'ZapIcon' };

const YT_OFFICIAL = '<span style="color:#FF0000;font-weight:600;font-size:12px">▶ YouTube</span><br><small>Métrica oficial de YouTube</small>';
const derived = (formula) => `<span style="color:#7367f0;font-weight:600;font-size:12px">⚙ Calculada</span><br><small><code>${formula}</code></small>`;

export default {
  name: 'ContentProfileAnalyticsYouTube',
  components: { BButton, BPopover },
  props: {
    channel: { type: Object, required: true },
    videos: { type: Array, default: () => [] },
    private_metrics: { type: Array, default: null },
  },
  data() {
    return {
      getFormat,
      TAB_ICONS,
      TAB_SVG_ICONS: {
        SHORTS: require('@/assets/images/icons/youtube-shorts.svg'),
        VOD: require('@/assets/images/icons/youtube-video.svg'),
      },
      active_tab: 'OVERALL',
      ageChartW: 220,
      ageChartH: 100,
      ageMarginL: 28,
      ageMarginR: 6,
      ageMarginT: 8,
      ageMarginB: 20,
    };
  },
  computed: {
    visible_videos() {
      return this.videos.slice(0, 5);
    },
    available_tabs() {
      if (!this.private_metrics) return [];
      const order = ['OVERALL', 'VOD', 'SHORTS'];
      return this.private_metrics
        .filter(s => s.video_count > 0 || s.views > 0)
        .sort((a, b) => order.indexOf(a.content_type) - order.indexOf(b.content_type))
        .map(s => ({ key: s.content_type, label: TAB_LABELS[s.content_type] || s.content_type }));
    },
    active_stat_data() {
      if (!this.private_metrics) return null;
      return this.private_metrics.find(s => s.content_type === this.active_tab) || null;
    },
    active_totals() {
      const s = this.active_stat_data;
      if (!s) return [];
      const nd = this.$t('search_preview.notAvailable');
      return [
        { label: 'Vistas', value: s.views !== null && s.views !== undefined ? getFormat(s.views) : nd, tooltip: YT_OFFICIAL },
        { label: 'Vistas con interacción', value: s.engaged_views !== null && s.engaged_views !== undefined ? getFormat(s.engaged_views) : nd, tooltip: YT_OFFICIAL },
        { label: 'Videos', value: s.video_count !== null && s.video_count !== undefined ? getFormat(s.video_count) : nd, tooltip: YT_OFFICIAL },
        { label: 'Espectadores únicos', value: s.unique_viewers !== null && s.unique_viewers !== undefined ? getFormat(s.unique_viewers) : nd, tooltip: YT_OFFICIAL },
        { label: 'Alcance único', value: s.unique_reach !== null && s.unique_reach !== undefined ? getFormat(s.unique_reach) : nd, tooltip: YT_OFFICIAL },
        { label: 'Likes', value: s.likes !== null && s.likes !== undefined ? getFormat(s.likes) : nd, tooltip: YT_OFFICIAL },
        { label: 'Comentarios', value: s.comments !== null && s.comments !== undefined ? getFormat(s.comments) : nd, tooltip: YT_OFFICIAL },
      ];
    },
    active_averages() {
      const s = this.active_stat_data;
      if (!s) return [];
      const nd = this.$t('search_preview.notAvailable');
      const pct = (v) => { return v !== null ? `${(v * 100).toFixed(2)} %` : nd; };
      const fmt = (v) => { return v !== null ? getFormat(Math.round(v)) : nd; };
      return [
        { label: 'Engagement Rate', value: s.engagement_rate !== null ? pct(s.engagement_rate) : nd, tooltip: derived('(likes + comentarios) / vistas × 100') },
        { label: 'Vistas prom.', value: s.avg_views_per_video !== null ? fmt(s.avg_views_per_video) : nd, tooltip: derived('vistas totales / número de videos') },
        { label: 'Vistas interacción prom.', value: s.avg_engaged_views_per_video !== null ? fmt(s.avg_engaged_views_per_video) : nd, tooltip: derived('vistas con interacción / número de videos') },
        { label: 'Alcance prom.', value: s.avg_reach_per_video !== null ? fmt(s.avg_reach_per_video) : nd, tooltip: derived('alcance único / número de videos') },
        { label: 'Likes prom.', value: s.avg_likes_per_video !== null ? fmt(s.avg_likes_per_video) : nd, tooltip: derived('likes totales / número de videos') },
        { label: 'Comentarios prom.', value: s.avg_comments_per_video !== null ? fmt(s.avg_comments_per_video) : nd, tooltip: derived('comentarios totales / número de videos') },
        { label: 'Guardados en playlist prom.', value: s.avg_videos_added_playlist !== null ? fmt(s.avg_videos_added_playlist) : nd, tooltip: derived('veces añadido a playlist / número de videos') },
      ];
    },
    ageGridLines() {
      const { ageChartH, ageMarginT, ageMarginB } = this;
      const innerH = ageChartH - ageMarginT - ageMarginB;
      return [0, 0.25, 0.5, 0.75, 1].map(f => (ageMarginT + (innerH * (1 - f))));
    },
    ageYLabels() {
      const { ageChartH, ageMarginT, ageMarginB } = this;
      const innerH = ageChartH - ageMarginT - ageMarginB;
      return [0, 25, 50, 75, 100].map(val => ({
        val,
        y: ageMarginT + (innerH * (1 - (val / 100))),
      }));
    },
    active_demographics() {
      const s = this.active_stat_data;
      if (!s) return null;
      const hasGender = s.gender_male > 0 || s.gender_female > 0;
      const AGE_FIELDS = [
        { label: '18-24', value: s.age_18_to_24 },
        { label: '25-34', value: s.age_25_to_34 },
        { label: '35-44', value: s.age_35_to_44 },
        { label: '45-54', value: s.age_45_to_54 },
        { label: '55-64', value: s.age_55_to_64 },
        { label: '65+', value: s.age_65_plus },
      ];
      const hasAge = AGE_FIELDS.some(a => a.value > 0);
      if (!hasGender && !hasAge && (!s.countries || !s.countries.length)) return null;

      // --- Pie paths ---
      const malePct = Math.round(s.gender_male * 100);
      const femalePct = 100 - malePct;
      const malePath = this.pieSlice(60, 60, 50, 0, (malePct / 100) * 360);
      const femalePath = this.pieSlice(60, 60, 50, (malePct / 100) * 360, 360);

      // --- Line chart points ---
      const { ageChartW, ageChartH, ageMarginL, ageMarginR, ageMarginT, ageMarginB } = this;
      const innerW = ageChartW - ageMarginL - ageMarginR;
      const innerH = ageChartH - ageMarginT - ageMarginB;
      const maxVal = Math.max(...AGE_FIELDS.map(a => a.value), 0.01);
      const age_points = AGE_FIELDS.map((a, i) => ({
        label: a.label,
        x: ageMarginL + ((i / (AGE_FIELDS.length - 1)) * innerW),
        y: ageMarginT + (innerH * (1 - (a.value / maxVal))),
      }));
      const age_path = this.smoothLine(age_points);

      return {
        has_gender: hasGender,
        male_pct: malePct,
        female_pct: femalePct,
        male_path: malePath,
        female_path: femalePath,
        has_age: hasAge,
        age_points,
        age_path,
        countries: s.countries || [],
      };
    },
    topic_labels() {
      const cats = this.channel.topic_categories;
      if (!cats || !cats.length) return [];
      const iconMap = {
        entertainment: 'StarIcon',
        music: 'MusicIcon',
        sports: 'ActivityIcon',
        gaming: 'ZapIcon',
        technology: 'CpuIcon',
        travel: 'MapIcon',
        food: 'CoffeeIcon',
        education: 'BookIcon',
        science: 'ApertureIcon',
        business: 'BriefcaseIcon',
        fashion: 'ScissorsIcon',
        health: 'HeartIcon',
        fitness: 'ActivityIcon',
        news: 'FileTextIcon',
        comedy: 'SmileIcon',
        film: 'FilmIcon',
        automotive: 'TruckIcon',
        lifestyle: 'SunIcon',
        charity: 'HeartIcon',
        collectibles: 'PackageIcon',
        accessories: 'StarIcon',
        'music tour': 'MusicIcon',
        lucha: 'ShieldIcon',
        nature: 'FeatherIcon',
        beauty: 'SmileIcon',
        art: 'PenToolIcon',
        cooking: 'CoffeeIcon',
        politics: 'GlobeIcon',
        religion: 'GlobeIcon',
        pets: 'HeartIcon',
      };
      return cats.map(url => {
        const slug = url.split('/wiki/').pop() || url;
        const label = slug.replace(/_/g, ' ');
        const icon = iconMap[label.toLowerCase()] || 'TagIcon';
        return { label, icon };
      });
    },
    channel_er() {
      const { view_count, engaged_view_count } = this.channel;
      if (!view_count || !engaged_view_count) return null;
      return (engaged_view_count / view_count) * 100;
    },
    channel_stats() {
      const nd = this.$t('search_preview.notAvailable');
      const { view_count, engaged_view_count, video_count } = this.channel;
      return [
        { label: 'Vistas totales', value: view_count ? getFormat(view_count) : nd, tooltip: YT_OFFICIAL },
        { label: 'Vistas con interacción', value: engaged_view_count ? getFormat(engaged_view_count) : nd, tooltip: YT_OFFICIAL },
        { label: 'Videos publicados', value: video_count !== null ? getFormat(video_count) : nd, tooltip: YT_OFFICIAL },
        {
          label: 'ER del canal',
          value: this.channel_er !== null ? `${this.channel_er.toFixed(2)} %` : nd,
          tooltip: derived('vistas con interacción / vistas totales × 100'),
        },
      ];
    },
  },
  watch: {
    available_tabs(tabs) {
      if (tabs.length) this.active_tab = tabs[0].key;
    },
  },
  methods: {
    pieSlice(cx, cy, r, startDeg, endDeg) {
      if (Math.abs(endDeg - startDeg) >= 359.9) {
        return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx - 0.01} ${cy - r} Z`;
      }
      const toRad = deg => ((deg - 90) * Math.PI) / 180;
      const sx = cx + (r * Math.cos(toRad(startDeg)));
      const sy = cy + (r * Math.sin(toRad(startDeg)));
      const ex = cx + (r * Math.cos(toRad(endDeg)));
      const ey = cy + (r * Math.sin(toRad(endDeg)));
      const large = endDeg - startDeg > 180 ? 1 : 0;
      return `M ${cx} ${cy} L ${sx} ${sy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey} Z`;
    },
    smoothLine(pts) {
      if (!pts.length) return '';
      let d = `M ${pts[0].x} ${pts[0].y}`;
      for (let i = 1; i < pts.length; i++) {
        const prev = pts[i - 1];
        const curr = pts[i];
        const cpx = (prev.x + curr.x) / 2;
        d += ` C ${cpx} ${prev.y} ${cpx} ${curr.y} ${curr.x} ${curr.y}`;
      }
      return d;
    },
    openVideo(url) {
      window.open(url, '_blank');
    },
    openChannel() {
      const handle = this.channel.handle || this.channel.youtube_id;
      window.open(`https://www.youtube.com/@${handle}`, '_blank');
    },
    onImgError(e) {
      e.target.src = require('@/assets/images/dashboard/img-default-reload.svg');
    },
  },
};
</script>

<style scoped lang="scss">
.mr-05 { margin-right: 0.3em; }
.mb-05 { margin-bottom: 0.3em; }
.mt-05 { margin-top: 0.3em; }
.f-12 { font-size: 12px; }
.gap-1 { gap: 0.25rem; }

.tab-switcher {
  display: flex;
  gap: 6px;

  .tab-btn {
    flex: 1;
    padding: 6px 10px;
    border: 1px solid #E2E8F0;
    border-radius: 6px;
    background: white;
    font-size: 13px;
    color: #6e6b7b;
    cursor: pointer;
    outline: none;
    transition: background 0.15s, color 0.15s, border-color 0.15s;

    &.active {
      background: #7367f0;
      color: white;
      border-color: #7367f0;

      img {
        filter: brightness(0) invert(1);
      }
    }

    &:not(.active):hover {
      border-color: #7367f0;
      color: #7367f0;
    }
  }
}

.grid-container {
  display: grid;
  cursor: pointer;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;

  .grid-item {
    background-color: white;
    position: relative;

    .img-cover {
      width: 100%;
      height: 135.5px;
      object-fit: cover;
    }

    .container-icon-heart {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      background: linear-gradient(180deg, rgba(0,0,0,0) 0, rgba(0,0,0,.8) 100%);
      border-bottom-right-radius: 6px;
      border-bottom-left-radius: 6px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-end;
      padding: 6px 8px 4px;
      gap: 2px;
      p {
        margin: 0;
        color: white;
        font-family: 'avenir-medium';
        font-size: 11px;
        overflow: hidden;
      }
    }

    .badge-content-type {
      position: absolute;
      top: 6px;
      right: 6px;
      background: rgba(0,0,0,0.65);
      color: white;
      font-size: 10px;
      padding: 2px 6px;
      border-radius: 4px;
    }

    &:nth-child(1) {
      grid-column: 1 / 3;
      grid-row: 1 / 3;
      .img-cover {
        border-top-left-radius: 6px;
        border-bottom-left-radius: 6px;
        height: 281px;
      }
    }
    &:nth-child(3) .img-cover { border-top-right-radius: 6px; }
    &:nth-child(5) .img-cover { border-bottom-right-radius: 6px; }
  }
}

.grid-analytics {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;

  @media (max-width: 400px) { grid-template-columns: repeat(1, 1fr); }

  .label-metric {
    font-size: 12px;
    line-height: 1.3;
  }

  .title-result {
    font-size: 18px;
    width: 100%;
    display: block;
  }
}

.box-style-analytics {
  border-radius: 6px;
  box-shadow: 0 1px 4px #0b14260a;
  padding: 16px;
  background-color: white;
  border: 1px solid #E2E8F0;

  .outline-secondary-custom-analysis {
    background: white;
    box-shadow: rgba(0,0,0,0.25) 0px 1px 4px;
    padding: 10px;
  }
}

.label-metric {
  font-size: 12px;
  line-height: 1.3;
  display: block;
}

// Demographics layout
.demo-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.gender-section {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;

  .pie-svg {
    width: 100px;
    height: 100px;
  }

  .gender-legend {
    margin-top: 8px;
    font-size: 12px;

    .legend-item {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-bottom: 2px;
    }

    .dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      display: inline-block;
      flex-shrink: 0;
      &--blue { background: #7367f0; }
      &--red  { background: #ea5455; }
    }
  }
}

.age-section {
  flex: 1;
  min-width: 0;

  .age-svg {
    width: 100%;
    overflow: visible;
  }

  .age-label-svg {
    font-size: 8px;
    fill: #adb5bd;
  }
}

.category-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.category-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #f8f8ff;
  border: 1px solid #ede9ff;
  min-width: 64px;
  cursor: default;

  .category-icon {
    color: #7367f0;
  }

  .category-name {
    font-size: 11px;
    color: #5e5873;
    text-align: center;
    white-space: nowrap;
  }
}

.section-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #adb5bd;
}

.cursor-pointer { cursor: pointer; }
.text-blue { color: #7367f0; }
.text-muted { color: #adb5bd; }
.text-underline { text-decoration: underline; }
</style>
